import { Avatar, Anchor, Group, Text } from '@mantine/core';
import { TextCell } from '@ayocore/platform/client/components/RTable/cells';
import { useDrawer } from '@ayocore/platform/client/hooks/useDrawer';
import { useUsersController } from '../UsersController';
import { apiBaseUrl } from '@ayocore/platform/client/helpers/fetcher';
import { getUserInitials } from '@ayocore/platform/client/helpers/userInitials';
import { UserProfileView } from '../components/UserProfileView';
import styles from './UserCell.module.css';

interface UserCellProps {
  user: { id: number; name?: string | null; email?: string | null } | null | undefined;
  /** Текст-заглушка при отсутствии пользователя */
  fallback?: string;
}

/**
 * Ячейка пользователя — аватарка + клик открывает профиль в дравере.
 * Данные (username, avatar URL) берутся из кеша UsersController.
 *
 * @example
 * cell: ({ row }) => <UserCell user={row.original.owner_user} />
 */
const UserCell = ({ user, fallback = '—' }: UserCellProps) => {
  const { getUser } = useUsersController();
  const profileDrawer = useDrawer(UserProfileView);

  if (!user)
    return (
      <Text size='xs' c='dimmed' style={{ fontStyle: 'italic' }}>
        {fallback}
      </Text>
    );

  const cached = getUser(user.id);
  const label = cached?.name || user.name || user.email || `ID: ${user.id}`;
  const username = cached?.username;
  const avatarUrl = cached?.avatarThumbUrl;

  // Абсолютные URL (S3) используем как есть, относительные — с apiBaseUrl
  const avatarSrc = avatarUrl
    ? avatarUrl.startsWith('http')
      ? avatarUrl
      : `${apiBaseUrl}${avatarUrl}`
    : null;

  const avatar = (
    <Avatar src={avatarSrc} size={24} radius='xl'>
      {getUserInitials(label)}
    </Avatar>
  );

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    profileDrawer.open(
      {
        userId: user.id,
        username: username ?? undefined,
        isModal: true,
        title: label,
      },
      { size: 'md' }
    );
  };

  if (username) {
    return (
      <Group gap={8} wrap='nowrap' className={styles.link}>
        {avatar}
        <Anchor
          component='button'
          type='button'
          size='xs'
          onClick={handleClick}
          className={styles.link}
        >
          {label}
        </Anchor>
      </Group>
    );
  }

  return (
    <Group gap={8} wrap='nowrap' className={styles.link}>
      {avatar}
      <TextCell value={label} />
    </Group>
  );
};

export default UserCell;
